/**
 * DomainSection — Paramètres › Domaine (Iter112, Feb 2026)
 *
 * Une seule carte, six sous-sections :
 *   1. Enregistrement (WHOIS)             — expiration, jours restants, registraire, renouvellement
 *   2. Sécurité & connectivité            — SSL + nameservers + boutons "Revérifier"
 *   3. Email professionnel                — statuts Zoho + Resend (SPF / DKIM / DMARC)
 *   4. Alertes                            — 3 pastilles avec deep-link vers Paramètres › Notifications email
 *   5. Vérifications automatiques         — bandeau info
 *   6. Historique des vérifications       — journal + encart diff DNS
 *
 * RBAC :
 *   • Premier Admin      : accès complet (bouton "Revérifier" actif)
 *   • Admin simple       : lecture seule (bouton grisé + tooltip)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { toast } from "@/lib/toast";
import {
  Globe, ShieldCheck, Mail, BellRing, Clock, History,
  Copy, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Building2, Server, LockIcon as LockIcon, ChevronRight, MinusCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

/* --------------------------------------------------------- utils */

function formatIsoDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function formatIsoDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function humanRelative(iso) {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diff = dt.getTime() - Date.now();
    const hours = Math.round(diff / 3600000);
    if (hours < 0) return `il y a ${Math.abs(hours)} h`;
    if (hours < 24) return `dans ${hours} h`;
    const days = Math.round(hours / 24);
    return `dans ${days} j`;
  } catch { return ""; }
}

function daysBadgeColor(days) {
  if (days == null) return { bg: "#3A3A3A", fg: "#9a9a95", border: "#3A3A3A" };
  if (days <= 15) return { bg: "transparent", fg: "#F87171", border: "#DC2626" };
  if (days <= 30) return { bg: "transparent", fg: "#FB923C", border: "#F97316" };
  if (days <= 60) return { bg: "transparent", fg: "#F9BC26", border: "#F9BC26" };
  return { bg: "transparent", fg: "#4ADE80", border: "#4ADE80" };
}

function copyToClipboard(text, label) {
  try {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  } catch {
    toast.error("Copie impossible");
  }
}

/* --------------------------------------------------------- atoms */

function CopyableValue({ value, label = "Valeur" }) {
  if (!value) return <span className="text-muted-foreground italic text-sm">—</span>;
  return (
    <div className="inline-flex items-center gap-2 max-w-full">
      <code
        className="text-xs font-mono truncate max-w-[420px]"
        style={{ color: "#EFEFEF" }}
        title={value}
      >
        {value}
      </code>
      <button
        type="button"
        onClick={() => copyToClipboard(value, label)}
        className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-vertec-primary hover:bg-white/5"
        title={`Copier ${label.toLowerCase()}`}
        data-testid={`domain-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Copy size={12} />
      </button>
    </div>
  );
}

function StatusPill({ ok, labelOk = "OK", labelKo = "À revérifier", size = "sm" }) {
  const style = ok
    ? { bg: "transparent", fg: "#4ADE80", border: "#4ADE80" }
    : { bg: "transparent", fg: "#F87171", border: "#EF4444" };
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-3 py-1 text-xs"}`}
      style={{ background: style.bg, color: style.fg, border: `1px solid ${style.border}` }}
    >
      <Icon size={size === "sm" ? 12 : 14} />
      {ok ? labelOk : labelKo}
    </span>
  );
}

function DaysLeftBadge({ days, thresholdWarn = 60, thresholdCrit = 30 }) {
  const c = daysBadgeColor(days);
  const critical = days != null && days <= thresholdCrit;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}
      title={days != null ? `${days} jour${days > 1 ? "s" : ""} restants` : "Inconnu"}
    >
      {critical && <AlertTriangle size={11} />}
      {days == null ? "—" : `${days} j`}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, tone = "primary" }) {
  const toneColor = tone === "primary" ? "#5b5fc7" : tone === "info" ? "#4A9EFF" : "#F9BC26";
  return (
    <div className="flex items-start gap-3 mb-3">
      <div
        className="shrink-0 h-8 w-8 rounded-md inline-flex items-center justify-center"
        style={{ background: "#141417", border: "1px solid #26262C", color: toneColor }}
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold" style={{ color: "#EFEFEF" }}>{title}</h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function KeyValueRow({ label, children, testId }) {
  return (
    <div className="flex items-start gap-4 py-2" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold w-40 shrink-0 pt-0.5">
        {label}
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

/* --------------------------------------------------------- diff card */

const DIFF_FIELD_LABELS = {
  nameservers: "Nameservers (NS)",
  spf: "SPF (v=spf1)",
  dkim_resend: "DKIM Resend",
  dmarc: "DMARC",
  zoho_verification_txt: "Vérification Zoho (TXT)",
};

function DnsDiffCard({ diff }) {
  return (
    <div
      className="mt-2 rounded-md border overflow-hidden"
      style={{ background: "#141417", borderColor: "#26262C", borderLeft: "3px solid #F97316" }}
    >
      <div className="px-3 py-1.5 flex items-center gap-2" style={{ borderBottom: "1px solid #26262C", boxShadow: "inset 3px 0 0 0 #F97316" }}>
        <AlertTriangle size={12} style={{ color: "#F97316" }} />
        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#F97316" }}>
          {diff.length} changement{diff.length > 1 ? "s" : ""} DNS détecté{diff.length > 1 ? "s" : ""}
        </span>
      </div>
      <div className="p-2 space-y-2">
        {diff.map((entry, i) => (
          <div key={i} className="rounded p-2" style={{ background: "#111" }}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "#5b5fc7" }}>
              {DIFF_FIELD_LABELS[entry.field] || entry.field}
            </div>
            <div
              className="text-[11px] font-mono px-2 py-1 rounded mb-1.5 whitespace-pre-wrap break-all"
              style={{
                background: "#17171B",
                color: "#F87171",
                borderLeft: "3px solid #DC2626",
                textDecoration: "line-through",
              }}
            >
              — {formatDnsValue(entry.before)}
            </div>
            <div
              className="text-[11px] font-mono px-2 py-1 rounded whitespace-pre-wrap break-all"
              style={{
                background: "#17171B",
                color: "#86EFAC",
                borderLeft: "3px solid #22C55E",
              }}
            >
              + {formatDnsValue(entry.after)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PROBE_LABEL = { whois: "WHOIS", ssl: "SSL", dns: "DNS" };

/** Reconstitue { ok, target, error } par sonde pour les entrées antérieures au bloc `probes`. */
function probesOf(h) {
  if (h.probes && Object.keys(h.probes).length) return h.probes;
  const legacy = {};
  for (const k of ["whois", "ssl", "dns"]) {
    const r = h[k] || {};
    if (r.ok === undefined) continue;
    legacy[k] = { ok: !!r.ok, target: r.host || r.domain || null, error: r.ok ? null : (r.error_human || r.error || null) };
  }
  return legacy;
}

function DnsCheckEntry({ h }) {
  const probes = probesOf(h);
  const keys = Object.keys(probes);
  const failed = keys.filter((k) => probes[k].ok === false);
  const okOthers = keys.filter((k) => probes[k].ok);
  const noDetail = !h.ok && failed.length === 0;
  const kindLabel = h.kind === "manual" ? "Manuel" : "Auto";
  return (
    <div
      className="rounded-md border px-3 py-2"
      style={{ background: "#1A1A1A", borderColor: h.dns_diff?.length ? "#F97316" : h.ok ? "#2A2A2A" : "#5a2328" }}
      data-testid={`domain-dnsChecks-entry-${h.id}`}
      data-day={h.day}
      data-kind={h.kind}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: h.ok ? "#4ADE80" : "#F87171" }} data-testid={`domain-dnsChecks-dot-${h.id}`} />
        <span className="text-xs font-mono" style={{ color: "#EFEFEF" }}>{formatIsoDateTime(h.ts)}</span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider"
          style={{
            background: "transparent",
            border: `1px solid ${h.kind === "manual" ? "#5b5fc7" : "#4A9EFF"}80`,
            color: h.kind === "manual" ? "#8B90E8" : "#7EB9FF",
          }}
          data-testid={`domain-dnsChecks-kind-${h.id}`}
        >
          {kindLabel}
        </span>
        {h.ok ? (
          <span className="text-[10px] text-muted-foreground" data-testid={`domain-dnsChecks-summary-${h.id}`}>
            SSL {h.ssl?.days_left ?? "?"}j · WHOIS {h.whois?.days_left ?? "?"}j · DNS OK
          </span>
        ) : (
          <span className="text-[10px] font-semibold" style={{ color: "#F87171" }} data-testid={`domain-dnsChecks-summary-${h.id}`}>
            {noDetail
              ? "Échec — détail non disponible (entrée antérieure à la mise à jour)"
              : `Échec — sonde${failed.length > 1 ? "s" : ""} ${failed.map((k) => PROBE_LABEL[k]).join(", ")}`}
          </span>
        )}
      </div>
      {!h.ok && failed.length > 0 && (
        <div className="mt-1.5 pl-4 text-[11px] leading-relaxed" style={{ color: "#f0a9a7" }} data-testid={`domain-dnsChecks-reason-${h.id}`}>
          {failed.map((k) => (
            <div key={k}>
              <span className="font-semibold">{PROBE_LABEL[k]}</span>
              {probes[k].target ? <span className="text-muted-foreground"> ({probes[k].target})</span> : null}
              {" : "}{probes[k].error || "motif non disponible"}
            </div>
          ))}
          {okOthers.length > 0 && (
            <div className="text-muted-foreground">
              {okOthers.map((k) => PROBE_LABEL[k]).join(okOthers.length === 2 ? " et " : ", ")} : OK
            </div>
          )}
        </div>
      )}
      {h.dns_diff && h.dns_diff.length > 0 && <DnsDiffCard diff={h.dns_diff} />}
    </div>
  );
}

function formatDnsValue(v) {
  if (v == null) return "(vide / absent)";
  if (Array.isArray(v)) return v.length === 0 ? "(liste vide)" : v.join("\n");
  return String(v);
}

/* --------------------------------------------------------- alerts pills */

const ALERT_KEYS = [
  { key: "system_ssl_expiry_admin", label: "Expiration SSL", subtitle: "seuil 30 j" },
  { key: "system_domain_expiry_admin", label: "Expiration domaine", subtitle: "seuil 60 j" },
  { key: "system_dns_unexpected_change_admin", label: "Changement DNS inattendu", subtitle: "diff avant/après dans l'email" },
];

function AlertsPills({ onOpenPrefs }) {
  const [prefsStats, setPrefsStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/email-prefs");
        if (!alive) return;
        // r.data has { keys: [...], prefs: {...}, stats: {...} } or similar
        const stats = r.data?.stats || {};
        const prefs = r.data?.prefs || {};
        const merged = {};
        for (const k of ALERT_KEYS.map((x) => x.key)) {
          merged[k] = {
            enabled: prefs[k] !== false,  // default is enabled
            sent: stats[k]?.sent || 0,
            failed: stats[k]?.failed || 0,
          };
        }
        setPrefsStats(merged);
      } catch (e) {
        // best-effort: pills still show, without counters
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="grid gap-2">
      {ALERT_KEYS.map((a) => {
        const s = prefsStats[a.key] || { enabled: true, sent: 0, failed: 0 };
        const active = !!s.enabled;
        return (
          <button
            key={a.key}
            type="button"
            onClick={() => onOpenPrefs(a.key)}
            className="group flex items-center gap-3 text-left rounded-md px-3 py-2 border transition-all hover:bg-white/5 hover:brightness-125"
            style={{
              background: "#1A1A1A",
              borderColor: active ? "#5b5fc744" : "#2A2A2A",
            }}
            data-testid={`domain-alert-${a.key}`}
            title={`Ouvrir Paramètres › Notifications email › ${a.label}`}
          >
            <span
              className="shrink-0 h-2 w-2 rounded-full"
              style={{ background: active ? "#4ADE80" : "#9a9a95" }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium" style={{ color: "#EFEFEF" }}>
                {a.label}
                <span className="text-[10px] text-muted-foreground ml-2 font-normal">
                  · {a.subtitle}
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {active ? "Activée" : "Désactivée"}
                {" · "}
                {loading ? "…" : (
                  <>
                    <span style={{ color: "#4ADE80" }}>{s.sent} envoyé{s.sent > 1 ? "s" : ""}</span>
                    {" · "}
                    <span style={{ color: s.failed > 0 ? "#F87171" : "#9a9a95" }}>
                      {s.failed} échec{s.failed > 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>
            </div>
            <ChevronRight size={14} className="shrink-0 text-muted-foreground group-hover:text-vertec-primary transition-colors" />
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------- MAIN */

export default function DomainSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = !!user?.is_first_admin;

  const [data, setData] = useState(null);
  const [dnsChecks, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechecking, setRechecking] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [statusR, histR] = await Promise.all([
        api.get("/domain/status"),
        api.get("/domain/history?limit=30"),
      ]);
      setData(statusR.data);
      setHistory(histR.data?.items || []);
    } catch (e) {
      toast.error("Impossible de charger le statut du domaine");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRecheck = async () => {
    if (!canWrite || rechecking) return;
    setRechecking(true);
    try {
      await api.post("/domain/recheck");
      toast.success("Vérification manuelle exécutée");
      await loadAll();
    } catch (e) {
      toast.error("La vérification a échoué");
    } finally {
      setRechecking(false);
    }
  };

  const openPrefs = (key) => {
    navigate(`/parametres?tab=email-prefs&highlight=${encodeURIComponent(key)}`);
  };

  const latest = data?.latest || {};
  const whois = latest.whois || {};
  const ssl = latest.ssl || {};
  const dns = latest.dns || {};
  const zohoMail = latest.zoho_mail || {};
  const rootHost = latest.root_host || {};
  const appHost = data?.app_host || rootHost.app_host;

  // iter186 — statuts basés sur le DNS uniquement (SPF : zohomail / resend / amazonses)
  const resendSpfOk = dns.spf_ok ?? !!(dns.spf && /include:.*(zohomail|zoho|resend|amazonses)/i.test(dns.spf));
  const resendDkimOk = !!dns.dkim_resend;
  const resendDmarcOk = !!dns.dmarc;

  return (
    <div className="space-y-4" data-testid="domain-section-root">
      <div>
        <h2 className="text-2xl font-semibold mb-1">Domaine</h2>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Surveillance du domaine <code className="text-xs">{data?.public_host || "?"}</code> :
          expiration, SSL, DNS, statuts email et alertes automatiques.
          {!canWrite && (
            <span className="ml-1 italic">Lecture seule — la modification est réservée au Premier Admin.</span>
          )}
        </p>
      </div>

      <Card className="p-5 space-y-6 border-vertec-borderDark bg-vertec-elevate">
        {loading && !data ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Chargement du statut du domaine…
          </div>
        ) : (
          <>
            {/* ============================ 1. ENREGISTREMENT ============================ */}
            <section data-testid="domain-section-registration">
              <SectionHeader
                icon={Globe}
                title="Enregistrement"
                subtitle="Informations WHOIS — expiration et registraire"
              />
              <div className="pl-11">
                <KeyValueRow label="Domaine" testId="domain-row-name">
                  <code className="text-sm font-mono" style={{ color: "#EFEFEF" }}>
                    {whois.domain || data?.public_host || "—"}
                  </code>
                </KeyValueRow>
                <KeyValueRow label="Expiration" testId="domain-row-expiration">
                  <span className="text-sm" style={{ color: "#EFEFEF" }}>{formatIsoDate(whois.expiration)}</span>
                  <DaysLeftBadge days={whois.days_left} thresholdWarn={60} thresholdCrit={30} />
                </KeyValueRow>
                <KeyValueRow label="Registraire" testId="domain-row-registrar">
                  <Building2 size={14} className="text-muted-foreground" />
                  <CopyableValue value={whois.registrar} label="Registraire" />
                </KeyValueRow>
                <KeyValueRow label="Renouvellement auto." testId="domain-row-autorenew">
                  <span className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-[11px]"
                        style={{ background: "#1A1A1A", border: "1px solid #3A3A3A", color: "#9a9a95" }}>
                    <LockIcon size={11} />
                    Non pilotable depuis Le Desk
                  </span>
                  <span className="text-[11px] text-muted-foreground italic">
                    Géré directement chez le registraire ({whois.registrar || "?"}).
                  </span>
                </KeyValueRow>
              </div>
            </section>

            <div className="h-px" style={{ background: "#2A2A2A" }} />

            {/* ============================ 2. SÉCURITÉ & CONNECTIVITÉ ============================ */}
            <section data-testid="domain-section-security">
              <div className="flex items-center justify-between mb-3 gap-4">
                <SectionHeader
                  icon={ShieldCheck}
                  title="Sécurité & connectivité"
                  subtitle="Certificat SSL et serveurs DNS"
                />
                {data?.next_check_at && (
                  <div
                    className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground shrink-0 whitespace-nowrap italic"
                    title={`Prochaine vérification automatique : ${formatIsoDateTime(data.next_check_at)}`}
                  >
                    <Clock size={11} />
                    Prochaine vérif. {humanRelative(data.next_check_at)}
                  </div>
                )}
              </div>
              <div className="pl-11">
                <KeyValueRow label="Certificat SSL" testId="domain-row-ssl">
                  {ssl.ok ? (
                    <>
                      <StatusPill ok labelOk="Valide" />
                      {ssl.host && <CopyableValue value={ssl.host} label="Hôte" />}
                      <span className="text-xs text-muted-foreground">
                        Expire le <strong style={{ color: "#EFEFEF" }}>{formatIsoDate(ssl.expiration)}</strong>
                      </span>
                      <DaysLeftBadge days={ssl.days_left} thresholdWarn={30} thresholdCrit={15} />
                    </>
                  ) : (
                    <>
                      <StatusPill ok={false} labelKo="Sonde échouée" />
                      <span className="text-[11px] text-muted-foreground italic">{ssl.error || ""}</span>
                    </>
                  )}
                </KeyValueRow>
                {rootHost.domain && rootHost.hosted === false && (
                  <KeyValueRow label={`Domaine racine ${rootHost.domain}`} testId="domain-row-root-host">
                    <span className="inline-flex items-center gap-1 rounded-full font-semibold px-2 py-0.5 text-[11px]" style={{ background: "transparent", color: "#a9a9b2", border: "1px solid #3a3a42" }} data-testid="domain-root-neutral-pill">
                      <MinusCircle size={12} /> Non hébergé
                    </span>
                    <span className="text-[11px] text-muted-foreground italic">{rootHost.note || `normal — app sur ${appHost}`}</span>
                  </KeyValueRow>
                )}
                {ssl.issuer && (
                  <KeyValueRow label="Émetteur SSL" testId="domain-row-ssl-issuer">
                    <span className="text-xs text-muted-foreground truncate max-w-[500px]" title={ssl.issuer}>
                      {ssl.issuer}
                    </span>
                  </KeyValueRow>
                )}
                <KeyValueRow label="Nameservers" testId="domain-row-nameservers">
                  <div className="flex flex-col gap-1">
                    {(dns.nameservers || []).map((ns, i) => (
                      <CopyableValue key={i} value={ns} label={`NS ${i + 1}`} />
                    ))}
                    {(dns.nameservers || []).length === 0 && (
                      <span className="text-muted-foreground italic text-sm">Aucun nameserver résolu</span>
                    )}
                  </div>
                </KeyValueRow>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecheck}
                    disabled={!canWrite || rechecking}
                    className="h-8 gap-2"
                    data-testid="domain-btn-recheck"
                    title={canWrite ? "Lancer une vérification manuelle immédiate" : "Action réservée au Premier Admin"}
                  >
                    <RefreshCw size={13} className={rechecking ? "animate-spin" : ""} />
                    {rechecking ? "Vérification…" : "Revérifier maintenant"}
                  </Button>
                  {latest.ts && (
                    <span className="text-[10px] text-muted-foreground italic">
                      Dernier check : {formatIsoDateTime(latest.ts)}
                      {latest.kind === "manual" ? " (manuel)" : " (auto)"}
                    </span>
                  )}
                </div>
              </div>
            </section>

            <div className="h-px" style={{ background: "#2A2A2A" }} />

            {/* ============================ 3. EMAIL PROFESSIONNEL ============================ */}
            <section data-testid="domain-section-email">
              <SectionHeader
                icon={Mail}
                title="Email professionnel"
                subtitle="Statuts de vérification Zoho Mail et Resend"
              />
              <div className="pl-11">
                <KeyValueRow label="Zoho Mail" testId="domain-row-zoho">
                  <StatusPill ok={!!zohoMail.ok} labelOk="Vérifié (DNS : MX Zoho)" labelKo="À vérifier (aucun MX Zoho)" />
                  {(zohoMail.mx || []).length > 0 && (
                    <div className="flex flex-col gap-1">
                      {zohoMail.mx.map((m, i) => <CopyableValue key={i} value={m} label={`MX ${i + 1}`} />)}
                    </div>
                  )}
                  {!zohoMail.ok && zohoMail.error && (
                    <span className="text-[11px] text-muted-foreground italic">{zohoMail.error}</span>
                  )}
                </KeyValueRow>
                <KeyValueRow label="Vérification Zoho (TXT)" testId="domain-row-zoho-txt">
                  {dns.zoho_verification_txt ? (
                    <>
                      <StatusPill ok labelOk="Présent" />
                      <CopyableValue value={dns.zoho_verification_txt} label="TXT" />
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full font-semibold px-2 py-0.5 text-[11px]" style={{ background: "transparent", color: "#a9a9b2", border: "1px solid #3a3a42" }} data-testid="domain-zoho-txt-optional">
                      <MinusCircle size={12} /> Non requis
                    </span>
                  )}
                </KeyValueRow>
                <KeyValueRow label="Resend (SPF)" testId="domain-row-resend-spf">
                  <StatusPill ok={resendSpfOk} labelOk="OK" labelKo="À vérifier" />
                  {dns.spf && <CopyableValue value={dns.spf} label="SPF" />}
                </KeyValueRow>
                <KeyValueRow label="Resend (DKIM)" testId="domain-row-resend-dkim">
                  <StatusPill ok={resendDkimOk} labelOk="Présent" labelKo="Absent" />
                  {dns.dkim_resend && (
                    <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[380px]">
                      resend._domainkey (clé publique)
                    </span>
                  )}
                </KeyValueRow>
                <KeyValueRow label="DMARC" testId="domain-row-resend-dmarc">
                  <StatusPill ok={resendDmarcOk} labelOk="Présent" labelKo="Non configuré" />
                  {dns.dmarc && <CopyableValue value={dns.dmarc} label="DMARC" />}
                </KeyValueRow>
              </div>
            </section>

            <div className="h-px" style={{ background: "#2A2A2A" }} />

            {/* ============================ 4. ALERTES ============================ */}
            <section data-testid="domain-section-alerts">
              <SectionHeader
                icon={BellRing}
                title="Alertes"
                subtitle="Raccourcis vers le catalogue Paramètres › Notifications email › Système"
              />
              <div className="pl-11">
                <AlertsPills onOpenPrefs={openPrefs} />
              </div>
            </section>

            <div className="h-px" style={{ background: "#2A2A2A" }} />

            {/* ============================ 5. VÉRIFS AUTO QUOTIDIENNES ============================ */}
            <section data-testid="domain-section-autocheck">
              <SectionHeader
                icon={Clock}
                title="Vérifications automatiques quotidiennes"
                subtitle="1× par jour, sans action de ta part"
                tone="info"
              />
              <div className="pl-11">
                <div
                  className="text-xs rounded-md px-3 py-2 border"
                  style={{ background: "#17171B", borderColor: "#26262C", borderLeft: "3px solid #4A9EFF", color: "#EFEFEF" }}
                >
                  Les sondes WHOIS (vert-tec.com), SSL ({appHost || "hôte de l'app"}), DNS (NS, MX, SPF, DKIM, DMARC) tournent automatiquement chaque jour — aucune vérification ne dépend du site racine.
                  Les résultats alimentent l'historique ci-dessous et déclenchent les alertes email
                  configurées ci-dessus si un seuil est franchi ou un changement DNS détecté.
                </div>
              </div>
            </section>

            <div className="h-px" style={{ background: "#2A2A2A" }} />

            {/* ============================ 6. HISTORIQUE ============================ */}
            <section data-testid="domain-section-dnsChecks">
              <SectionHeader
                icon={History}
                title="Historique des vérifications"
                subtitle={`${dnsChecks.length} entrée${dnsChecks.length > 1 ? "s" : ""} — la plus récente en premier`}
              />
              <div className="pl-11">
                {dnsChecks.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic py-3">
                    Aucune vérification enregistrée.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                    {dnsChecks.map((h) => <DnsCheckEntry key={h.id} h={h} />)}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </Card>
    </div>
  );
}
